import { GraphQLVolunteerStatus } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isVolunteerSlotCancellable } from '@sola/shared-utils/supporters/volunteers.ts';
import {
  daysUntil,
  formatSlotDate,
  formatSlotTime,
  toIsoString,
} from '@sola/utils/date.ts';

export type SlotCardContactInput =
  | string
  | {
      email?: string;
      id?: string;
      name: string;
    };

type VolunteerStatusBadgeColor = 'negative' | 'positive' | 'warning';

const volunteerStatusBadgeColors = {
  [GraphQLVolunteerStatus.Approved]: 'positive',
  [GraphQLVolunteerStatus.Available]: 'warning',
  [GraphQLVolunteerStatus.Canceled]: 'negative',
  [GraphQLVolunteerStatus.Completed]: 'positive',
  [GraphQLVolunteerStatus.CompletedNoShow]: 'negative',
  [GraphQLVolunteerStatus.Confirmed]: 'positive',
  [GraphQLVolunteerStatus.Declined]: 'negative',
  [GraphQLVolunteerStatus.Pending]: 'warning',
  [GraphQLVolunteerStatus.Waitlist]: 'warning',
} as const satisfies Record<GraphQLVolunteerStatus, VolunteerStatusBadgeColor>;

const getContactName = (contact: SlotCardContactInput) =>
  typeof contact === 'string' ? contact : contact.name;

interface GetVolunteerSlotCardDataProps {
  contacts: SlotCardContactInput[];
  currentDate?: Date | string;
  endTime: Date | string;
  language?: string;
  startTime: Date | string;
  status?: GraphQLVolunteerStatus | null;
}

/**
 * Derive display values and action state for a volunteer slot card.
 *
 * Confirmation is available only for approved shifts occurring within the
 * next seven calendar days. Ended and terminal-status shifts hide both
 * actions, while a confirmed upcoming shift continues to allow cancellation.
 */
export const getVolunteerSlotCardData = ({
  contacts,
  currentDate,
  endTime,
  language = 'en-US',
  startTime,
  status,
}: GetVolunteerSlotCardDataProps) => {
  const daysUntilShiftEnd = daysUntil(endTime, currentDate);
  const hasEnded = daysUntilShiftEnd !== undefined && daysUntilShiftEnd < 0;
  const isCancelHidden = hasEnded || !isVolunteerSlotCancellable(status);
  const daysUntilShift = daysUntil(startTime, currentDate);
  const isWithinConfirmationWindow =
    daysUntilShift !== undefined && daysUntilShift >= 0 && daysUntilShift <= 7;

  const startStr = formatSlotTime(startTime, language);
  const endStr = formatSlotTime(endTime, language);

  const formattedDate = formatSlotDate(startTime, 'long', true, language);

  return {
    badgeColor: status ? volunteerStatusBadgeColors[status] : undefined,
    contactList: contacts.map((c) => ({
      name: getContactName(c),
    })),
    dateTime:
      typeof startTime === 'string' ? startTime : toIsoString(startTime),
    formattedDate,
    isCancelHidden,
    isConfirmDisabled:
      status !== GraphQLVolunteerStatus.Approved || !isWithinConfirmationWindow,
    isConfirmHidden:
      isCancelHidden || status === GraphQLVolunteerStatus.Confirmed,
    timeRange: startStr && endStr ? `${startStr} – ${endStr}` : '',
  };
};
